import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, Mail, Calendar, Tag, MessageSquare, X, Save, Trash2,
  Inbox, CheckCircle2, Clock, AlertCircle, RefreshCw, Filter, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { adminAuth, leadsApi, formatApiErrorDetail } from "../lib/adminApi";

const STATUS_CONFIG = {
  new: { label: "New", color: "#FF2222", bg: "bg-[#FF2222]", text: "text-[#FF2222]", border: "border-[#FF2222]", icon: AlertCircle },
  contacted: { label: "Contacted", color: "#FFB020", bg: "bg-[#FFB020]", text: "text-[#FFB020]", border: "border-[#FFB020]", icon: Clock },
  closed: { label: "Closed", color: "#4ADE80", bg: "bg-[#4ADE80]", text: "text-[#4ADE80]", border: "border-[#4ADE80]", icon: CheckCircle2 },
};

const fmtDate = (d) => {
  try {
    const dt = new Date(d);
    return dt.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return "—"; }
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, closed: 0 });
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const fetchAll = useCallback(async (currentFilter = filter) => {
    setLoading(true);
    try {
      const [leadsRes, statsRes] = await Promise.all([
        leadsApi.list(currentFilter || undefined),
        leadsApi.stats(),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (!adminAuth.getToken()) {
      navigate("/admin", { replace: true });
      return;
    }
    fetchAll();
  }, [navigate, fetchAll]);

  const logout = () => {
    adminAuth.logout();
    navigate("/admin", { replace: true });
  };

  const updateLead = async (id, payload) => {
    try {
      const { data } = await leadsApi.update(id, payload);
      setLeads((prev) => prev.map((l) => (l.id === id ? data : l)));
      if (selected?.id === id) setSelected(data);
      const statsRes = await leadsApi.stats();
      setStats(statsRes.data);
      toast.success("Updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Update failed");
    }
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await leadsApi.remove(id);
      setLeads((prev) => prev.filter((l) => l.id !== id));
      if (selected?.id === id) setSelected(null);
      const statsRes = await leadsApi.stats();
      setStats(statsRes.data);
      toast.success("Lead deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  const setStatusFilter = (s) => {
    setFilter(s);
    fetchAll(s);
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Top bar */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#050505]/85 border-b border-white/10">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <a href="/" className="font-display text-xl md:text-2xl uppercase tracking-tight flex items-baseline gap-2 hover:text-[#FF2222] transition-colors">
              iza3nee <span className="text-[#FF2222]">creations</span>
            </a>
            <span className="hidden md:inline text-[10px] uppercase tracking-[0.3em] text-white/40">/ Lead Management</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh-btn"
              onClick={() => fetchAll()}
              className="border border-white/15 hover:border-[#FF2222] hover:text-[#FF2222] text-white px-3 py-2 transition-colors"
              title="Refresh"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="border border-white/15 hover:border-[#FF2222] hover:text-[#FF2222] text-white uppercase tracking-wider text-xs px-4 py-2 inline-flex items-center gap-2 transition-colors"
            >
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto px-6 md:px-10 py-8 md:py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-8">
          <StatCard label="Total Leads" value={stats.total} accent="#fff" testid="stat-total" />
          <StatCard label="New" value={stats.new} accent="#FF2222" testid="stat-new" />
          <StatCard label="Contacted" value={stats.contacted} accent="#FFB020" testid="stat-contacted" />
          <StatCard label="Closed" value={stats.closed} accent="#4ADE80" testid="stat-closed" />
        </div>

        {/* Filters */}
        <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
          <div className="flex items-center gap-2 flex-wrap">
            <Filter size={14} className="text-white/40" />
            <FilterPill active={filter === ""} onClick={() => setStatusFilter("")} testid="filter-all">All</FilterPill>
            <FilterPill active={filter === "new"} onClick={() => setStatusFilter("new")} testid="filter-new" color="#FF2222">New</FilterPill>
            <FilterPill active={filter === "contacted"} onClick={() => setStatusFilter("contacted")} testid="filter-contacted" color="#FFB020">Contacted</FilterPill>
            <FilterPill active={filter === "closed"} onClick={() => setStatusFilter("closed")} testid="filter-closed" color="#4ADE80">Closed</FilterPill>
          </div>
          <div className="text-xs text-white/50 uppercase tracking-[0.2em]">
            {leads.length} {leads.length === 1 ? "lead" : "leads"}
          </div>
        </div>

        {/* Leads table */}
        <div className="border border-white/10 bg-[#0a0a0a]" data-testid="leads-table">
          <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 border-b border-white/10 text-[10px] uppercase tracking-[0.25em] text-white/40 bg-[#0d0d0d]">
            <div className="col-span-3">Lead</div>
            <div className="col-span-3">Email</div>
            <div className="col-span-3">Subject</div>
            <div className="col-span-2">Date</div>
            <div className="col-span-1 text-right">Status</div>
          </div>

          {loading && leads.length === 0 && (
            <div className="px-5 py-12 text-center text-white/40 text-sm">Loading leads...</div>
          )}

          {!loading && leads.length === 0 && (
            <div className="px-5 py-16 text-center">
              <Inbox size={32} className="text-white/20 mx-auto mb-3" />
              <div className="text-white/60 uppercase tracking-[0.2em] text-xs">No leads in this view</div>
              <div className="text-white/30 text-xs mt-2">When customers submit the contact form, they'll appear here.</div>
            </div>
          )}

          {leads.map((lead) => {
            const cfg = STATUS_CONFIG[lead.status] || STATUS_CONFIG.new;
            return (
              <button
                key={lead.id}
                data-testid={`lead-row-${lead.id}`}
                onClick={() => setSelected(lead)}
                className="w-full text-left grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-5 py-4 border-b border-white/5 hover:bg-white/5 transition-colors items-center"
              >
                <div className="md:col-span-3 flex items-center gap-3 min-w-0">
                  <div className={`w-1.5 h-10 ${cfg.bg} flex-shrink-0`} />
                  <div className="min-w-0">
                    <div className="text-white font-medium truncate">{lead.name}</div>
                    <div className="md:hidden text-xs text-white/50 truncate">{lead.email}</div>
                  </div>
                </div>
                <div className="hidden md:block md:col-span-3 text-sm text-white/70 truncate">{lead.email}</div>
                <div className="md:col-span-3 text-sm text-white/60 truncate">{lead.subject || <span className="text-white/30 italic">No subject</span>}</div>
                <div className="md:col-span-2 text-xs text-white/50 uppercase tracking-wider">{fmtDate(lead.created_at)}</div>
                <div className="md:col-span-1 md:text-right">
                  <span className={`inline-flex items-center gap-1.5 ${cfg.text} text-[10px] uppercase tracking-[0.2em] font-bold`}>
                    <cfg.icon size={11} /> {cfg.label}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </main>

      {/* Detail drawer */}
      {selected && (
        <LeadDetailDrawer
          lead={selected}
          onClose={() => setSelected(null)}
          onUpdate={updateLead}
          onDelete={deleteLead}
        />
      )}
    </div>
  );
}

const StatCard = ({ label, value, accent, testid }) => (
  <div data-testid={testid} className="bg-[#050505] p-5 md:p-6">
    <div className="text-[10px] uppercase tracking-[0.25em] text-white/50">{label}</div>
    <div className="font-display text-4xl md:text-5xl mt-2 leading-none" style={{ color: accent }}>{value}</div>
  </div>
);

const FilterPill = ({ active, onClick, children, color = "#fff", testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`text-[11px] uppercase tracking-[0.2em] font-bold px-3 py-1.5 border transition-colors ${
      active
        ? "bg-white text-black border-white"
        : "bg-transparent border-white/15 text-white/70 hover:border-white/40"
    }`}
    style={active ? {} : { color }}
  >
    {children}
  </button>
);

function LeadDetailDrawer({ lead, onClose, onUpdate, onDelete }) {
  const [notes, setNotes] = useState(lead.notes || "");
  const [savingNotes, setSavingNotes] = useState(false);
  const cfg = STATUS_CONFIG[lead.status] || STATUS_CONFIG.new;

  useEffect(() => { setNotes(lead.notes || ""); }, [lead.id, lead.notes]);

  const saveNotes = async () => {
    setSavingNotes(true);
    await onUpdate(lead.id, { notes });
    setSavingNotes(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="lead-detail-drawer">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <aside className="relative w-full max-w-2xl h-full bg-[#0a0a0a] border-l border-white/10 overflow-y-auto">
        <div className="sticky top-0 z-10 bg-[#0a0a0a]/95 backdrop-blur border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className={`inline-flex items-center gap-1.5 ${cfg.text} text-[10px] uppercase tracking-[0.25em] font-bold border ${cfg.border} px-2 py-1`}>
              <cfg.icon size={11} /> {cfg.label}
            </span>
            <span className="text-[10px] uppercase tracking-[0.25em] text-white/40">Lead Detail</span>
          </div>
          <button data-testid="drawer-close" onClick={onClose} className="text-white/60 hover:text-white p-2">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-6 space-y-7">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">From</div>
            <h2 className="font-display uppercase text-white text-3xl md:text-4xl leading-none tracking-tight">{lead.name}</h2>
            <a
              href={`mailto:${lead.email}?subject=Re:%20${encodeURIComponent(lead.subject || 'Your iza3nee inquiry')}`}
              className="inline-flex items-center gap-2 mt-3 text-[#FF2222] hover:text-white transition-colors"
              data-testid="drawer-reply-mailto"
            >
              <Mail size={14} /> {lead.email} <ExternalLink size={12} />
            </a>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Meta icon={Tag} label="Subject" value={lead.subject || "—"} />
            <Meta icon={Calendar} label="Submitted" value={fmtDate(lead.created_at)} />
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2 flex items-center gap-2">
              <MessageSquare size={11} /> Message
            </div>
            <div className="bg-[#050505] border-l-2 border-[#FF2222] p-4 whitespace-pre-wrap text-white/90 leading-relaxed text-sm">
              {lead.message}
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3">Status</div>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(STATUS_CONFIG).map(([key, c]) => {
                const active = lead.status === key;
                return (
                  <button
                    key={key}
                    data-testid={`status-btn-${key}`}
                    onClick={() => !active && onUpdate(lead.id, { status: key })}
                    className={`text-[11px] uppercase tracking-[0.2em] font-bold py-3 border transition-all flex items-center justify-center gap-2 ${
                      active
                        ? "bg-white text-black border-white"
                        : "bg-transparent border-white/15 text-white/70 hover:border-white/40"
                    }`}
                    style={active ? {} : { color: c.color }}
                  >
                    <c.icon size={12} /> {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">Private Notes</div>
            <textarea
              data-testid="drawer-notes"
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add internal notes about this lead..."
              className="iza-input w-full resize-none"
            />
            <div className="flex justify-end mt-2">
              <button
                data-testid="drawer-save-notes"
                onClick={saveNotes}
                disabled={savingNotes || notes === (lead.notes || "")}
                className="bg-[#FF2222] hover:bg-white hover:text-black text-white uppercase font-bold tracking-wider text-xs px-5 py-2.5 inline-flex items-center gap-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Save size={13} /> {savingNotes ? "Saving..." : "Save Notes"}
              </button>
            </div>
          </div>

          <div className="border-t border-white/10 pt-5 flex flex-wrap gap-3 justify-between items-center">
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/30">
              ID: {lead.id.slice(0, 8)}
              {lead.notification_sent ? " · notified" : ""}
              {lead.autoreply_sent ? " · auto-replied" : ""}
            </div>
            <button
              data-testid="drawer-delete"
              onClick={() => onDelete(lead.id)}
              className="border border-white/15 hover:border-[#FF2222] hover:text-[#FF2222] text-white/70 uppercase tracking-wider text-[11px] px-4 py-2 inline-flex items-center gap-2 transition-colors"
            >
              <Trash2 size={12} /> Delete
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}

const Meta = ({ icon: Icon, label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-1 flex items-center gap-1.5">
      <Icon size={11} /> {label}
    </div>
    <div className="text-white text-sm">{value}</div>
  </div>
);
